import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useLocation } from '../context/LocationContext';
import { calculateCart } from '../utils/apiTransform';
import './Checkout.css';

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, clearCart } = useCart();
  const { selectedLocation } = useLocation();
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [cartCalculation, setCartCalculation] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState(null);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    pickupDate: '',
    pickupTime: '',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    specialInstructions: '',
  });

  // Calculate cart totals whenever cart items change
  useEffect(() => {
    const fetchCartCalculation = async () => {
      if (cartItems.length === 0) {
        setCartCalculation(null);
        setCalculationError(null);
        return;
      }

      if (!selectedLocation) {
        return;
      }

      try {
        setIsCalculating(true);
        setCalculationError(null);

        const response = await calculateCart(cartItems, selectedLocation);

        if (response.status === 'error') {
          // Clear cart and show error message
          setCalculationError('Server encountered an error. Cart has been reset.');
          clearCart();
          setCartCalculation(null);
        } else {
          setCartCalculation(response);
        }
      } catch (error) {
        console.error('Error calculating cart:', error);
        setCalculationError('Failed to calculate cart totals. Please try again.');
      } finally {
        setIsCalculating(false);
      }
    };

    fetchCartCalculation();
  }, [cartItems, selectedLocation, clearCart]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate order processing
    setOrderPlaced(true);
    setTimeout(() => {
      clearCart();
      navigate('/');
    }, 3000);
  };

  if (cartItems.length === 0 && !orderPlaced) {
    navigate('/cart');
    return null;
  }

  if (orderPlaced) {
    return (
      <div className="checkout-page">
        <div className="order-success">
          <div className="success-icon">✓</div>
          <h2>Order Placed Successfully!</h2>
          <p>Thank you for your order. You will receive a confirmation email shortly.</p>
          <p className="redirect-message">Redirecting to menu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <h2 className="checkout-title">Checkout</h2>

        {calculationError && (
          <div className="error-message">
            <p>{calculationError}</p>
          </div>
        )}

        <div className="checkout-content">
          <form className="checkout-form" onSubmit={handleSubmit}>
            <div className="form-section">
              <h3>Contact Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fullName">Full Name *</label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Pickup Information</h3>
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Pickup Location</label>
                  <div className="location-display-box">
                    {selectedLocation}
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Pickup Date *</label>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="pickupDate"
                        value="November 26"
                        checked={formData.pickupDate === 'November 26'}
                        onChange={handleInputChange}
                        required
                      />
                      <span className="radio-label">November 26</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="pickupDate"
                        value="November 27"
                        checked={formData.pickupDate === 'November 27'}
                        onChange={handleInputChange}
                        required
                      />
                      <span className="radio-label">November 27</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="pickupDate"
                        value="November 28"
                        checked={formData.pickupDate === 'November 28'}
                        onChange={handleInputChange}
                        required
                      />
                      <span className="radio-label">November 28</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="pickupDate"
                        value="November 29"
                        checked={formData.pickupDate === 'November 29'}
                        onChange={handleInputChange}
                        required
                      />
                      <span className="radio-label">November 29</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="pickupTime">Pickup Time *</label>
                  <input
                    type="time"
                    id="pickupTime"
                    name="pickupTime"
                    value={formData.pickupTime}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Payment Information</h3>
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="cardNumber">Card Number *</label>
                  <input
                    type="text"
                    id="cardNumber"
                    name="cardNumber"
                    value={formData.cardNumber}
                    onChange={handleInputChange}
                    placeholder="1234 5678 9012 3456"
                    maxLength="19"
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="cardName">Cardholder Name *</label>
                  <input
                    type="text"
                    id="cardName"
                    name="cardName"
                    value={formData.cardName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="expiryDate">Expiry Date *</label>
                  <input
                    type="text"
                    id="expiryDate"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleInputChange}
                    placeholder="MM/YY"
                    maxLength="5"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cvv">CVV *</label>
                  <input
                    type="text"
                    id="cvv"
                    name="cvv"
                    value={formData.cvv}
                    onChange={handleInputChange}
                    placeholder="123"
                    maxLength="4"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Special Instructions</h3>
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="specialInstructions">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    id="specialInstructions"
                    name="specialInstructions"
                    value={formData.specialInstructions}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Any special requests or dietary restrictions?"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="place-order-btn"
              disabled={!cartCalculation || cartCalculation.status !== 'success' || isCalculating}
            >
              {isCalculating ? 'Calculating...' : `Place Order - $${cartCalculation?.cart_summary?.total.toFixed(2) || '0.00'}`}
            </button>
          </form>

          <div className="order-summary">
            <h3>Order Summary</h3>
            <div className="summary-items">
              {cartItems.map((item) => {
                // Find matching item from API response if available
                const apiItem = cartCalculation?.items?.find(i => i.item === item.id);

                return (
                  <div key={item.id} className="summary-item">
                    <div className="summary-item-info">
                      <span className="summary-item-name">{item.name}</span>
                      <span className="summary-item-qty">x{item.quantity}</span>
                    </div>
                    <span className="summary-item-price">
                      ${apiItem ? apiItem.item_total.toFixed(2) : (item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="summary-totals">
              {isCalculating ? (
                <div className="summary-loading">
                  <p>Calculating totals...</p>
                </div>
              ) : cartCalculation && cartCalculation.status === 'success' ? (
                <>
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>${cartCalculation.cart_summary.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Tax</span>
                    <span>${cartCalculation.cart_summary.tax.toFixed(2)}</span>
                  </div>
                  <div className="summary-row total">
                    <span>Total</span>
                    <span>${cartCalculation.cart_summary.total.toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <div className="summary-row">
                  <span>Calculating...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
